// Ekran 09 — "Manuel hareket" drawer'ı.
// İşaretleme ref_files/btys-erp-09-stok-hareketleri.html drawerHTML()'inden:
//   .drawer.sm > .dr-h(.dr-t + .dr-s) + .dr-b > .fgrid + .dr-f(Vazgeç · Kaydet)

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { stokKartlari } from '../masterdata/api'
import { hareketler } from './api'
import { sayiyaCevir } from '../../lib/trFormat'
import { birimSecenekleri, useBirimler } from '../../lib/birimler'

/**
 * Referansta bu liste 4 sabit seçenekti (SM-0021, SM-0018, SM-0003, YK-0001).
 * Gerçek kartlardan doldurmak zorunlu: sabit liste yeni bir sarf kartı
 * eklendiğinde sessizce eskir.
 */
const MANUEL_TURLER = ['sarf', 'yedek', 'yakit'] as const

export default function ManuelHareketDrawer() {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()

  const [yon, setYon] = useState(0)
  const [kartId, setKartId] = useState('')
  const [miktar, setMiktar] = useState('1')
  const [birim, setBirim] = useState('adet')
  const [gerekce, setGerekce] = useState('')

  const { data: kartlar = [] } = useQuery({
    queryKey: ['kartlar', 'manuel-hareket'],
    queryFn: async () => {
      const hepsi = await Promise.all(MANUEL_TURLER.map((t) => stokKartlari.liste(t)))
      return hepsi.flat()
    },
  })

  // Birim sözlüğü — serbest metin alanı yalnız kartla değil, sistemin geri
  // kalanıyla da çelişen bir değer girilmesine açıktı ("ton" / "t" / "TON").
  const { data: sozluk = [] } = useBirimler()
  const birimler = birimSecenekleri(sozluk.map((b) => b.kod), [birim])

  // Kart seçilince birimi ondan al — referansın serbest metin alanı, kartın
  // birimiyle çelişen bir değer girilmesine açıktı.
  const secili = kartlar.find((k) => k.id === kartId)

  // Referansta listenin ilk seçeneği (SM-0021) açılışta seçilidir; "Seçin…"
  // yer tutucusu yoktu ve drawer açılır açılmaz Kaydet'e basılabiliyordu.
  // Liste sunucudan geldiği için ilk kart ancak yüklendikten sonra seçilebilir.
  useEffect(() => {
    if (kartId === '' && kartlar.length > 0) {
      setKartId(kartlar[0].id)
      setBirim(kartlar[0].birim)
    }
  }, [kartlar, kartId])

  const kaydet = useMutation({
    mutationFn: () =>
      hareketler.manuel({
        yon,
        stokKartiId: kartId,
        miktar: sayiyaCevir(miktar),
        birim,
        gerekce,
      }),
    onSuccess: (sonuc) => {
      kuyruk.invalidateQueries({ queryKey: ['hareketler'] })
      kuyruk.invalidateQueries({ queryKey: ['hareket-ozeti'] })
      toast(sonuc.mesaj)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Hareket kaydedilemedi'),
  })

  // Gerekçe kapısı sunucuda da var (K39, 409). Buradaki kilit yalnız kullanıcıyı
  // boşuna gidiş dönüşten kurtarıyor; asıl doğrulama sunucuda.
  const gecerli = kartId !== '' && gerekce.trim() !== '' && sayiyaCevir(miktar) !== 0

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>İşlem</label>
            <select className="in" value={yon} onChange={(e) => setYon(Number(e.target.value))}>
              <option value={0}>Düzeltme (sayım)</option>
              <option value={1}>Giriş</option>
              <option value={-1}>Çıkış</option>
            </select>
          </div>

          <div className="fld">
            <label>Stok kartı</label>
            <select
              className="in"
              value={kartId}
              onChange={(e) => {
                setKartId(e.target.value)
                const k = kartlar.find((x) => x.id === e.target.value)
                if (k) setBirim(k.birim)
              }}
            >
              {kartlar.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kod} {k.ad}
                </option>
              ))}
            </select>
          </div>

          <div className="fld">
            <label>Miktar</label>
            <input
              className="in mono"
              value={miktar}
              onChange={(e) => setMiktar(e.target.value)}
            />
          </div>

          <div className="fld">
            <label>Birim</label>
            <select
              className="in mono"
              value={birim}
              onChange={(e) => setBirim(e.target.value)}
            >
              {birimler.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            {secili && secili.birim !== birim && (
              <div className="fld-hint">Kart birimi: {secili.birim}</div>
            )}
          </div>

          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Gerekçe (zorunlu)</label>
            <input
              className="in"
              placeholder="Örn. fiziki sayım farkı — depo D-2"
              value={gerekce}
              onChange={(e) => setGerekce(e.target.value)}
            />
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!gecerli || kaydet.isPending}
          title={gecerli ? undefined : 'Stok kartı, sıfırdan farklı miktar ve gerekçe zorunlu'}
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}

